import { GlLoadingIcon, GlTableLite, GlButton, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { createAlert } from '../../../alert.js?vue3';
import { TYPENAME_ISSUE, TYPENAME_MERGE_REQUEST } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { TYPE_ISSUE } from '~/issues/constants';
import { localeDateFormat, newDate, parseSeconds, stringifyTime } from '~/lib/utils/datetime_utility';
import { __, s__ } from '~/locale';
import { timelogQueries } from '../../queries/constants';
import deleteTimelogMutation from '../../queries/delete_timelog.mutation.graphql';
export default {
    name: 'TimeTrackingReport',
    i18n: {
        deleteButtonText: s__('TimeTracking|Delete time spent')
    },
    components: {
        GlLoadingIcon,
        GlTableLite,
        GlButton
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    inject: {
        issuableType: {
            default: null
        }
    },
    props: {
        limitToHours: {
            type: Boolean,
            default: false,
            required: false
        },
        issuableId: {
            type: String,
            required: false,
            default: ''
        },
        timelogs: {
            type: Array,
            required: false,
            default: undefined
        },
        workItemIid: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: [
        'timelog-deleted'
    ],
    data () {
        return {
            report: this.timelogs ?? [],
            removingIds: []
        };
    },
    apollo: {
        report: {
            query () {
                return timelogQueries[this.issuableType].query;
            },
            variables () {
                const type = this.issuableType === TYPE_ISSUE ? TYPENAME_ISSUE : TYPENAME_MERGE_REQUEST;
                return {
                    id: convertToGraphQLId(type, this.issuableId),
                    useWorkItemFeatures: Boolean(this.glFeatures.workItemFeaturesField)
                };
            },
            update (data) {
                const timelogs = data?.issuable?.timelogs?.nodes || [];
                return timelogs.slice().sort((a, b)=>new Date(a.spentAt) - new Date(b.spentAt));
            },
            error () {
                createAlert({
                    message: __('Something went wrong. Please try again.')
                });
            },
            skip () {
                // In the work-item context, `timelogs` is provided by the parent
                // `WorkItemTimeTracking` component (which has its own apollo query).
                // For legacy issue/MR sidebar use, we fetch via `timelogQueries[issuableType]`.
                return Boolean(this.timelogs) || Boolean(this.workItemIid);
            }
        }
    },
    computed: {
        isLoading () {
            return this.$apollo.queries.report.loading;
        },
        totalTimeSpent () {
            const seconds = this.report.reduce((acc, item)=>acc + item.timeSpent, 0);
            return this.formatTimeSpent(seconds);
        }
    },
    watch: {
        timelogs (timelogs) {
            this.report = timelogs;
        }
    },
    methods: {
        isDeletingTimelog (timelogId) {
            return this.removingIds.includes(timelogId);
        },
        formatDate (date) {
            return localeDateFormat.asDateTimeFull.format(newDate(date));
        },
        formatShortDate (date) {
            return localeDateFormat.asDate.format(newDate(date));
        },
        getSummary (summary, note) {
            return summary ?? note?.body;
        },
        formatTimeSpent (seconds) {
            const negative = seconds < 0;
            return (negative ? '- ' : '') + stringifyTime(parseSeconds(seconds, {
                limitToHours: this.limitToHours
            }));
        },
        deleteTimelog (timelogId) {
            this.removingIds.push(timelogId);
            this.$apollo.mutate({
                mutation: deleteTimelogMutation,
                variables: {
                    input: {
                        id: timelogId
                    }
                }
            }).then(({ data })=>{
                if (data.timelogDelete?.errors?.length) {
                    throw new Error(data.timelogDelete.errors[0]);
                }
                // Notify the parent so it can update its own cache (e.g. the work-item
                // time tracking widget query) with the authoritative server response.
                this.$emit('timelog-deleted', {
                    timelogId,
                    data
                });
            }).catch((error)=>{
                createAlert({
                    message: s__('TimeTracking|An error occurred while removing the timelog.'),
                    captureError: true,
                    error
                });
            }).finally(()=>{
                this.removingIds.splice(this.removingIds.indexOf(timelogId), 1);
            });
        }
    },
    fields: [
        {
            key: 'spentAt',
            label: __('Date'),
            tdClass: 'gl-w-1/4'
        },
        {
            key: 'timeSpent',
            label: __('Time spent'),
            tdClass: 'gl-w-15'
        },
        {
            key: 'user',
            label: __('User')
        },
        {
            key: 'summary',
            label: __('Summary')
        },
        {
            key: 'actions',
            label: '',
            tdClass: 'gl-w-10'
        }
    ]
};
